// https://stackoverflow.com/questions/62210286/declare-type-with-react-useimperativehandle
// https://github.com/jaredpalmer/formik/issues/1603#issuecomment-575669249

import { HStack, Stack } from "@chakra-ui/react";
import { Form as FormikForm, Formik, FormikHelpers } from "formik";
import React from "react";
import * as Yup from "yup";
import { FormInputControl } from "../../../components/forms";
import {
  FormSelectControl,
  ISelectOption,
} from "../../../components/forms/SelectControl";
import { FormSingleSwitchControl } from "../../../components/forms/Switch";
import { useListOrganizations } from "../../../services/api/organization/queries/useListOrganizations";
import { useListSchemaVersions } from "../../../services/api/schemas/queries/useListSchemaVersions";
import {
  initialRegistryWorkingForm,
  IRegistry,
  IRegistryCreate,
  IRegistryWorkingForm,
  RegistryTypeEnum,
} from "../models/Registry";

export type FormHandle = {
  submitForm: () => void;
};

interface IProps {
  registry?: IRegistry;
  onSubmit: (data: IRegistryCreate) => Promise<void>;
}

export const RegistryForm = React.forwardRef<FormHandle, IProps>(
  ({ onSubmit, registry }, ref) => {
    React.useImperativeHandle(ref, () => ({
      // start() has type inferrence here
      submitForm() {
        bagRef.current.submitForm();
      },
    }));

    const { data: dataOrganizations } = useListOrganizations();
    const { data: dataSchemaVersions } = useListSchemaVersions();

    const bagRef = React.useRef<any>();

    const [workingFormValues, set_workingFormValues] =
      React.useState<IRegistryWorkingForm>(initialRegistryWorkingForm);

    const [organizationOptions, set_organizationOptions] = React.useState<
      ISelectOption[]
    >([]);
    const [registryTypes] = React.useState<ISelectOption[]>([
      {
        label: "Redcap",
        value: RegistryTypeEnum.REDCAP,
      },
      {
        label: "Geneial S3",
        value: RegistryTypeEnum.GENEIAL_S3,
      },
    ]);

    const [schemaVersionOptions, set_schemaVersionOptions] = React.useState<
      ISelectOption[]
    >([]);

    const RegistryFormsSchema = Yup.object().shape({
      title: Yup.string().required("Name is required"),
      description: Yup.string().required("Description is required"),
      is_active: Yup.boolean(),
      is_allow_edit: Yup.boolean(),
      is_allow_add: Yup.boolean(),
      registry_brand_url: Yup.string(),
      registry_type: Yup.string(),
      organization_id: Yup.string().required("Organization is required"),
      geneial_schema_version_id: Yup.string().required("Schema is required"),
      config_redcap_api_endpoint: Yup.string()
        .nullable()
        .when("registry_type", {
          is: (val) => val === RegistryTypeEnum.REDCAP,
          then: Yup.string().required("Api Endpoint is required"),
        }),
      config_redcap_api_key: Yup.string()
        .nullable()
        .when("registry_type", {
          is: (val) => val === RegistryTypeEnum.REDCAP,
          then: Yup.string().required("API Key is required"),
        }),
      config_geneial_s3_bucketname: Yup.string()
        .nullable()
        .when("registry_type", {
          is: (val) => val === RegistryTypeEnum.GENEIAL_S3,
          then: Yup.string().required("Bucket name is required"),
        }),
    });

    const handleOnSubmit = async (
      values,
      actions: FormikHelpers<IRegistryWorkingForm>
    ) => {
      actions.setStatus();
      actions.setSubmitting(true);

      const createRegistry: IRegistryCreate = {
        ...values,
      };

      try {
        // Pass up logic
        await onSubmit(createRegistry);
      } catch (error) {
        // Error submitting form, set status for dispaly
        actions.setStatus("Failed to update message. Please try again.");
      }

      actions.setSubmitting(false);
    };

    React.useEffect(() => {
      if (registry) {
        // Need to maintain not 'null' value
        registry.config_redcap_api_endpoint =
          registry.config_redcap_api_endpoint || "";
        registry.config_redcap_api_key = registry.config_redcap_api_key || "";

        set_workingFormValues(registry);
      }
    }, [registry]);

    React.useEffect(() => {
      let organizationOptionsUpdate: ISelectOption[] = [];
      if (dataOrganizations) {
        organizationOptionsUpdate = dataOrganizations.map((organization) => ({
          label: organization.name,
          value: organization.id,
        }));
      }

      set_organizationOptions(organizationOptionsUpdate);
    }, [dataOrganizations]);

    React.useEffect(() => {
      let schemaVersionsOptionsUpdate: ISelectOption[] = [];

      if (dataSchemaVersions) {
        schemaVersionsOptionsUpdate = dataSchemaVersions
          .filter((schema) => !schema.schema_is_abstract)
          .map((schema) => ({
            label: `${schema.schema_title} | v ${schema.version_major}.${schema.version_minor}.${schema.version_patch}`,
            value: schema.id,
          }));
      }

      set_schemaVersionOptions(schemaVersionsOptionsUpdate);
    }, [dataSchemaVersions]);

    return (
      <Formik
        innerRef={bagRef}
        initialValues={workingFormValues}
        validationSchema={RegistryFormsSchema}
        onSubmit={(values, actions) => {
          handleOnSubmit(values, actions);
        }}
        enableReinitialize={true}
      >
        {(props) => {
          return (
            <FormikForm>
              <Stack spacing={6}>
                <FormInputControl
                  label={"Registry Name"}
                  name="title"
                  formControlProps={{
                    maxW: "28rem",
                  }}
                />
                <FormInputControl
                  label={"Registry Description"}
                  name="description"
                />
                <FormInputControl
                  label={"Registry Image URL"}
                  name="registry_brand_url"
                />

                <HStack>
                  <FormSingleSwitchControl
                    label={"Active"}
                    name="is_active"
                  ></FormSingleSwitchControl>

                  <FormSingleSwitchControl
                    label={"Allow Edits"}
                    name="is_allow_edit"
                  ></FormSingleSwitchControl>

                  <FormSingleSwitchControl
                    label={"Allow Add"}
                    name="is_allow_add"
                  ></FormSingleSwitchControl>
                </HStack>

                <FormSelectControl
                  label={"Organization"}
                  name="organization_id"
                  placeholder="Select an organization..."
                  options={organizationOptions}
                ></FormSelectControl>

                <FormSelectControl
                  label={"Schema Version"}
                  name="geneial_schema_version_id"
                  placeholder="Select version..."
                  options={schemaVersionOptions}
                ></FormSelectControl>

                <FormSelectControl
                  label={"Registry Type"}
                  name="registry_type"
                  placeholder="Select a type..."
                  options={registryTypes}
                ></FormSelectControl>

                {props.values.registry_type === RegistryTypeEnum.REDCAP && (
                  <>
                    <FormInputControl
                      label={"RedCap Endpoint"}
                      name="config_redcap_api_endpoint"
                    />
                    <FormInputControl
                      label={"RedCap Api Key"}
                      name="config_redcap_api_key"
                    />
                  </>
                )}
                {props.values.registry_type === RegistryTypeEnum.GENEIAL_S3 && (
                  <>
                    <FormInputControl
                      label={"Geneial S3 Bucket Name"}
                      name="config_geneial_s3_bucketname"
                    />
                  </>
                )}
              </Stack>
            </FormikForm>
          );
        }}
      </Formik>
    );
  }
);
